// src/utils/guestAccess.ts
//
// Guest discovery (App Store Guideline 5.1.1(v), same product rules on web and
// mobile): signed-out visitors browse stylists, profiles, services, prices,
// portfolio, reviews and availability, and can pick a date/time. Sign-in is
// required before Review & Book and for favorites, messaging and account pages.
//
// Guests use the public /browse routes; signed-in clients keep /client/*.
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../hooks/useRedux';

export const GUEST_SIGN_IN_MESSAGES = {
  booking: 'Sign in or create your LookBel account to continue booking.',
  favorites: 'Sign in to save stylists to your favorites.',
  messaging: 'Sign in to message this stylist.',
} as const;

export type GuestSignInReason = keyof typeof GUEST_SIGN_IN_MESSAGES;

export type MarketplacePaths = {
  browse: string;
  stylist: (stylistRef: string) => string;
  booking: (stylistRef: string) => string;
};

export function marketplacePaths(inClientArea: boolean): MarketplacePaths {
  return inClientArea
    ? {
        browse: '/client/browse',
        stylist: (ref) => `/client/stylist/${ref}`,
        booking: () => '/client/booking',
      }
    : {
        browse: '/browse',
        stylist: (ref) => `/browse/${ref}`,
        booking: (ref) => `/browse/${ref}/book`,
      };
}

/** Links stay inside whichever area (guest /browse or client /client) the page is rendered in. */
export function useMarketplacePaths(): MarketplacePaths {
  const { pathname } = useLocation();
  return marketplacePaths(pathname.startsWith('/client'));
}

export function useIsSignedIn(): boolean {
  return useAppSelector((state) => Boolean(state.auth.token && state.auth.user));
}

/**
 * Sends a guest to /login with a reason banner. `returnTo` is where LoginForm
 * takes them after a successful sign-in (defaults to the current page).
 */
export function useRequireSignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (reason: GuestSignInReason, returnTo?: string) => {
      try {
        sessionStorage.setItem('redirectAfterLogin', returnTo || location.pathname);
      } catch {
        // storage unavailable (private mode): the user still reaches login
      }
      navigate('/login', { state: { message: GUEST_SIGN_IN_MESSAGES[reason] } });
    },
    [navigate, location.pathname],
  );
}

// ── Booking hand-off across sign-in ─────────────────────────────────────────
// Only the selection is carried (stylist, service, date, time) — never payment,
// loyalty or address state. The slot is re-validated when BookingPage restores it.

const PENDING_BOOKING_KEY = 'lookbel.pendingBooking';
const PENDING_BOOKING_MAX_AGE_MS = 30 * 60 * 1000;

export type PendingBooking = {
  bookingData: {
    stylistId: string;
    serviceId: string;
    service: { name: string; price: number; duration: number; description?: string; category?: string };
    stylist?: { name?: string; profilePhoto?: string };
  };
  /** yyyy-MM-dd */
  selectedDate: string;
  /** ISO start time of the chosen slot */
  slotStartTime: string;
  savedAt: number;
};

export function savePendingBooking(pending: Omit<PendingBooking, 'savedAt'>, now = Date.now()) {
  try {
    sessionStorage.setItem(PENDING_BOOKING_KEY, JSON.stringify({ ...pending, savedAt: now }));
  } catch {
    // ignore: the user can re-pick the slot after signing in
  }
}

export function clearPendingBooking() {
  try {
    sessionStorage.removeItem(PENDING_BOOKING_KEY);
  } catch {
    // ignore
  }
}

/** Reads the pending booking without clearing it (safe inside render / state initializers). */
export function peekPendingBooking(now = Date.now()): PendingBooking | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(PENDING_BOOKING_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PendingBooking;
    if (
      !parsed?.bookingData?.stylistId ||
      !parsed.bookingData.serviceId ||
      !parsed.selectedDate ||
      !parsed.slotStartTime ||
      now - Number(parsed.savedAt) > PENDING_BOOKING_MAX_AGE_MS
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Returns the pending booking once (and clears it); stale or malformed entries are dropped. */
export function consumePendingBooking(now = Date.now()): PendingBooking | null {
  const pending = peekPendingBooking(now);
  clearPendingBooking();
  return pending;
}
